import ky, { isHTTPError } from "ky";
import { createUnauthorizedHook } from "@/apis/clients/kyHooks";
import { API_BASE_ADMIN } from "@/constants/api";
import {
  toCreateBody,
  toExternalUser,
  toInviteSent,
  toPatchBody,
  type CreateExternalUserInput,
  type ExternalUser,
  type ExternalUserRowResponse,
  type MutationResponse,
  type UpdateExternalUserInput,
} from "@/types/externalUser";

/**
 * Surfaces the API's own reason for a refusal.
 *
 * ky's own message is a status line ("Request failed with status code 400
 * Bad Request..."); this API answers a refusal with `{"error": "..."}`,
 * written for the person reading it. Without this every "narrow your
 * search", "an account already exists for that address" and "no such
 * agency" would be shown as a status line instead.
 *
 * ky (2.0.2) already parses a JSON error body into `error.data` before
 * `beforeError` hooks run, fully consuming the response stream to do so —
 * `error.response.clone()` has nothing left to read by the time this runs,
 * so `error.data` is the only way to reach the body here.
 */
export const applyApiErrorMessage = ({ error }: { error: Error }): Error => {
  if (
    isHTTPError(error) &&
    typeof error.data === "object" &&
    error.data !== null
  ) {
    const { error: reason } = error.data as { error?: unknown };
    if (typeof reason === "string" && reason) {
      // eslint-disable-next-line no-param-reassign
      error.message = reason;
    }
  }
  return error;
};

const adminClient = ky.create({
  prefix: API_BASE_ADMIN,
  credentials: "same-origin",
  hooks: {
    afterResponse: [createUnauthorizedHook()],
    beforeError: [applyApiErrorMessage],
  },
});

interface ListResponse {
  users: ExternalUserRowResponse[];
}

/**
 * One user, with the agency resolved.
 *
 * The list cannot carry agency — there is no bulk lookup for it — so the edit
 * form fetches the user it is editing. That matters beyond display: the form
 * decides what changed by comparing against the agency currently held, and
 * without a real value every save looks like an agency change, rewriting the
 * grant and revoking the user's sessions.
 */
export const getExternalUser = async (id: number): Promise<ExternalUser> =>
  toExternalUser(
    await adminClient.get(`users/${id}`).json<ExternalUserRowResponse>()
  );

/**
 * Every user this service knows, with the profile T1 holds for each.
 *
 * One upstream call regardless of how many there are, which is what lets the
 * screen open on the whole list instead of asking for an agency first. The
 * agency itself is absent — it cannot be had in bulk — so rows carry a null
 * `agencyId`.
 */
export const listAllExternalUsers = async (): Promise<ExternalUser[]> => {
  const body = await adminClient.get("users").json<ListResponse>();
  return body.users.map(toExternalUser);
};

/**
 * The users of one agency.
 *
 * There is no all-users call. What a row displays lives in T1, and the
 * affordable way to read it is one agency-scoped query, so the screen asks
 * about an agency or it asks nothing.
 */
export const listExternalUsersByAgency = async (
  agencyId: number
): Promise<ExternalUser[]> => {
  const body = await adminClient
    .get("users", { searchParams: { agency_id: agencyId } })
    .json<ListResponse>();
  return body.users.map(toExternalUser);
};

/**
 * Users matching an address, whatever agency they hold.
 *
 * This is the only way to reach someone whose T1 permissions were moved out of
 * the configured organisation: they hold no agency the picker offers, so no
 * agency listing can show them.
 */
export const searchExternalUsers = async (
  query: string
): Promise<ExternalUser[]> => {
  const body = await adminClient
    .get("users", { searchParams: { search: query } })
    .json<ListResponse>();
  return body.users.map(toExternalUser);
};

export const createExternalUser = async (
  input: CreateExternalUserInput
): Promise<{ id: number; inviteSent: boolean }> => {
  const body = await adminClient
    .post("users", { json: toCreateBody(input) })
    .json<MutationResponse>();
  return { id: body.id, inviteSent: toInviteSent(body) };
};

export const updateExternalUser = async (
  id: number,
  patch: UpdateExternalUserInput
): Promise<void> => {
  await adminClient.patch(`users/${id}`, { json: toPatchBody(patch) }).json();
};

export const resendInvite = async (
  id: number
): Promise<{ inviteSent: boolean }> => {
  const body = await adminClient
    .post(`users/${id}/invite`)
    .json<MutationResponse>();
  return { inviteSent: toInviteSent(body) };
};
